import React, { useEffect, useState, useMemo } from 'react';
import api from '../utils/api';
import { getStoredUser } from '../controllers/authController';
import BillSummaryCards from '../components/BillSummaryCards';
import BillHistoryChart from '../components/BillHistoryChart';
import BillBreakdownChart from '../components/BillBreakdownChart';

// Trigger rebuild to pick up newly added components
const BillReconciliationTab = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [pieData, setPieData] = useState(null);
    const [forecastData, setForecastData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Memoize getting username
    const storedUser = useMemo(() => getStoredUser(), []);
    const username = storedUser?.username || '';

    useEffect(() => {
        if (!username) {
            setLoading(false);
            setError("User not authenticated.");
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Fetch API in parallel
                const [dashboardRes, pieRes, forecastRes] = await Promise.all([
                    api.get(`/dashboard-charts/${username}`),
                    api.get(`/bill-pie-chart/${username}`),
                    api.get(`/bill_forecast`, { params: { username } }).catch(err => {
                        console.warn("Could not fetch forecast data:", err);
                        return { data: null };
                    })
                ]);

                if (dashboardRes.data && dashboardRes.data.status === 'success') {
                    setDashboardData(dashboardRes.data);
                }

                if (pieRes.data && pieRes.data.status === 'success') {
                    setPieData(pieRes.data);
                }

                if (forecastRes.data && forecastRes.data.status === 'success') {
                    setForecastData(forecastRes.data);
                }
            } catch (err) {
                console.error("Error fetching bill reconciliation data:", err);

                const errorMsg = err.response?.data?.detail || err.message || "";

                if (errorMsg.includes("No billing data found") || errorMsg.includes("404")) {
                    setError("NO_DATA");
                } else {
                    setError("Failed to load bill data. Please try again.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [username]);

    // Helper to get the next month formatted as MMM-YY
    const getNextMonthLabel = (recentMonthStr) => {
        if (!recentMonthStr || recentMonthStr === "N/A") return "Next 30 Days";
        const parts = recentMonthStr.split('-');
        if (parts.length !== 2) return "Next 30 Days";

        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const monthIndex = months.findIndex(m => m.toLowerCase() === parts[0].toLowerCase());

        if (monthIndex === -1) return "Next 30 Days";

        let nextMonthIndex = (monthIndex + 1) % 12;
        let nextYearNum = parseInt(parts[1], 10);

        if (nextMonthIndex === 0 && !isNaN(nextYearNum)) {
            nextYearNum += 1;
        }

        const nextYearStr = !isNaN(nextYearNum)
            ? (parts[1].length === 2 ? nextYearNum.toString().padStart(2, '0') : nextYearNum.toString())
            : parts[1];

        return `${months[nextMonthIndex]}-${nextYearStr}`;
    };

    // Extract Summary Card Values
    const recentBill = dashboardData?.charts?.bill_graph?.historical_data?.at(-1) || 0;
    const recentUnits = dashboardData?.charts?.units_graph?.historical_data?.at(-1) || 0;
    const forecastBill = forecastData?.forecasted_bill_next_30_days || 0;
    const forecastUnits = forecastData?.forecasted_units_next_30_days || 0;

    const analyzedDays = forecastData?.analyzed_days || 0;
    const lastReadingDate = forecastData?.date_range_used?.end || null;
    let dailyAverageUsage = null;
    if (analyzedDays > 0 && forecastData?.total_consumed_in_range != null) {
        dailyAverageUsage = forecastData.total_consumed_in_range / analyzedDays;
    }

    // Derive Labels
    const labels = dashboardData?.charts?.labels || [];
    // The backend might append "Forecast" to labels
    let recentMonthLabel = "N/A";

    if (labels.length > 0) {
        if (labels[labels.length - 1] === "Forecast") {
            recentMonthLabel = labels.length > 1 ? labels[labels.length - 2] : "N/A";
        } else {
            recentMonthLabel = labels[labels.length - 1];
        }
    }

    const forecastMonthLabel = getNextMonthLabel(recentMonthLabel);

    // Prepare chart data for BillHistoryChart
    let chartsData = null;
    if (dashboardData?.charts) {
        chartsData = JSON.parse(JSON.stringify(dashboardData.charts)); // deep copy
        if (forecastData) {
            // Add Forecast to chartsData
            if (chartsData.labels && chartsData.labels[chartsData.labels.length - 1] !== "Forecast") {
                chartsData.labels.push("Forecast");
                chartsData.bill_graph = chartsData.bill_graph || {};
                chartsData.bill_graph.forecast_value = forecastData.forecasted_bill_next_30_days || 0;
                chartsData.units_graph = chartsData.units_graph || {};
                chartsData.units_graph.forecast_value = forecastData.forecasted_units_next_30_days || 0;
            }
        }
    }

    if (loading) {
        return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading Bill Reconciliation Data...</div>
    }

    if (error === "NO_DATA") {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                minHeight: '80vh', backgroundColor: '#f8fafc', padding: '40px', textAlign: 'center'
            }}>
                <div style={{ backgroundColor: '#fff', padding: '40px', borderRadius: '16px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', maxWidth: '500px' }}>
                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '24px' }}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                        <path d="M8 14h.01"></path>
                        <path d="M12 14h.01"></path>
                        <path d="M16 14h.01"></path>
                        <path d="M8 18h.01"></path>
                        <path d="M12 18h.01"></path>
                        <path d="M16 18h.01"></path>
                    </svg>
                    <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#0f172a', marginBottom: '16px' }}>No Billing Data Available For You</h2>
                </div>
            </div>
        );
    }

    if (error) {
        return <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
    }

    return (
        <div style={{ padding: '24px', backgroundColor: '#f8fafc', minHeight: '100vh', boxSizing: 'border-box' }}>
            {/* 4 Cards */}
            <BillSummaryCards
                recentBill={recentBill}
                recentUnits={recentUnits}
                forecastBill={forecastBill}
                forecastUnits={forecastUnits}
                dailyAverageUsage={dailyAverageUsage}
                analyzedDays={analyzedDays}
                lastReadingDate={lastReadingDate}
                recentMonthLabel={recentMonthLabel}
                forecastMonthLabel={forecastMonthLabel}
            />



            {/* Charts Box */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
                <div style={{ flex: 2, minWidth: '60%' }}>
                    <BillHistoryChart chartData={chartsData} />
                </div>
                <div style={{ flex: 1, minWidth: '30%' }}>
                    <BillBreakdownChart chartData={pieData} />
                </div>
            </div>
        </div>
    );
};

export default BillReconciliationTab;

